import { AvatarColors } from "@deligoo/ui"
import dayjs from "dayjs"

import { globalStore } from "@/App"
import { Driver, PaymentForm, TodayBreak } from "@/types"
import { escapeRegex } from "@/utils"
import { DriversPaymentsTab, DriversStatsTab } from "@/views/MapView/partials/Drivers/DriversPanel"
import { MapLayer } from "@/views/MapView/partials/MapLayers"

type AvatarColor = keyof typeof AvatarColors

export namespace DriverUtils {
  export function isOnline(driver: Driver) {
    return driver.state !== "offline"
  }

  export function isAfterShift(driver: Driver) {
    const { shiftEnd } = DriverUtils.getCurrentShiftHours(driver) || {}
    return shiftEnd ? dayjs(shiftEnd, "HH:mm").isBefore(dayjs()) : false
  }

  export function getAvatarColor(driver: Driver): AvatarColor {
    const COLORS_COUNT = Object.keys(AvatarColors).length
    // TODO

    return Object.keys(AvatarColors)[driver.id % COLORS_COUNT] as AvatarColor
  }

  export function getAvatarIndicator(driver: Driver) {
    const { is_on_break, state } = driver

    if (is_on_break && state !== "offline") return "break"

    return undefined
  }

  export function getAvatarDotColor(driver: Driver) {
    const { state, late } = driver

    if (state === "offline") return "muted"

    return late > 10 ? "alert" : late > 5 ? "warning" : "success"
  }

  export function getBatteryLevelColor(level: Driver["phone_battery_level"]) {
    return level > 40 ? "success" : level > 15 ? "warning" : "alert"
  }

  export function getDelayColor(delay: Driver["late"]) {
    return delay > 10 ? "alert" : delay > 0 ? "warning" : "info"
  }

  export function getPaymentColor(paymentForm: PaymentForm, formsUsedByDriver: PaymentForm[]) {
    return formsUsedByDriver.includes(paymentForm) ? "success" : "alert"
  }

  export function filterByTab(driver: Driver, tab: "free" | "busy" | "offline" | null) {
    return tab ? driver.state === tab : true
  }

  export function filterBySearch(driver: Driver, searchValue: string) {
    if (searchValue) {
      const searchValues = escapeRegex(searchValue).trim().split(" ")

      const regExps = searchValues.map((searchValue) => new RegExp(searchValue, "i"))

      return regExps.every((regex) => regex.test(driver.name) || regex.test(String(driver.phone)))
    }
    return true
  }

  export function filterByStats(driver: Driver, tab: DriversStatsTab) {
    const {
      teamsStats: { entities: teamsStats },
    } = globalStore.getState()

    const teamStats = teamsStats[driver.team_id]
    if (!teamStats) return false

    const { inactive_drivers_during_graphic_ids, blocked_drivers_ids, late_drivers_ids } = teamStats

    switch (tab) {
      case "inactive": {
        return inactive_drivers_during_graphic_ids.includes(driver.id)
      }
      case "blocked": {
        return blocked_drivers_ids.includes(driver.id)
      }
      case "late": {
        return late_drivers_ids.includes(driver.id)
      }
    }
  }

  export function filterByPayments(driver: Driver, tab: DriversPaymentsTab) {
    switch (tab) {
      case "no_card":
        return !driver.payment_forms.includes("card")
      case "no_cash":
        return !driver.payment_forms.includes("cash")
    }
  }

  export function filterByLayers(driver: Driver, layers: MapLayer[]) {
    const shouldShowOnline = layers.includes("online_drivers")
    const shouldShowOffline = layers.includes("offline_drivers")

    return (shouldShowOnline && DriverUtils.isOnline(driver)) || (shouldShowOffline && !DriverUtils.isOnline(driver))
  }

  export function formatAvailability(datetime: string) {
    const plRelative = {
      name: "pl-relative",
      relativeTime: {
        future: "za %s",
        past: "%s temu",
        s: "teraz",
        m: "minuty",
        mm: "%d minut",
        h: "godziny",
        hh: "%d godzin",
        d: "wczoraj",
        dd: "%d dni",
        M: "miesiąca",
        MM: "%d msc.",
        y: "roku",
        yy: "%d lat",
      },
    }

    return ` od ${dayjs(datetime)
      .locale(plRelative as ILocale) // TODO: extend locale?
      .fromNow(true)}`
  }

  export function getSystem(drivers_app_version: Driver["drivers_app_version"]) {
    return drivers_app_version?.split(" ")[1]
  }

  export function getAppVersion(drivers_app_version: Driver["drivers_app_version"]) {
    return drivers_app_version?.split(" ")[0]
  }

  export function getCurrentWorkingHours(driver: Driver) {
    return driver.updated_working_hours || driver.working_hours
  }

  export function getCurrentShiftHours(driver: Driver) {
    const workingHours = DriverUtils.getCurrentWorkingHours(driver)
    const [, shiftStart, shiftEnd] = workingHours?.match(/(\d\d:\d\d).+(\d\d:\d\d)/) || []
    if (!shiftStart || !shiftEnd) return undefined
    return { shiftStart, shiftEnd }
  }

  export function getNextTodayBreak(driver: Driver) {
    const { today_breaks } = driver
    return today_breaks.find((todayBreak) => todayBreak.next)
  }

  export function getBreakDuration(todayBreak: TodayBreak) {
    const { start_time, end_time } = todayBreak
    return dayjs(end_time, "HH:mm").diff(dayjs(start_time, "HH:mm"), "minutes")
  }

  export function getStateColor(state: Driver["state"]) {
    return state === "free" ? "success" : state === "busy" ? "info" : "muted"
  }
}
